import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircleQuestion, Paperclip, Send, Sparkles } from "lucide-react";
import { useApi, useInvalidateOn } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, InlineAlert, MarkdownBody, Textarea, cn, useSave } from "@ncp/ui";

interface ThreadMessage { id: string; at: string; from: "reporter" | "ministry"; subject: string; subjectAr?: string | null; body: string; bodyAr?: string | null }
interface ThreadView { messages: ThreadMessage[]; attachments: Array<{ id: string; at: string; fileName: string; sizeBytes: number; extractionStatus: string }>; awaitingReporter: boolean }
interface ReportGaps { isAnonymous: boolean; observedFrom?: string | null; evidenceDescription?: string | null; allegedEntities: string }

const MAX_QUESTION = 4000;
/** Wording that asks a reporter who they are – a question to an anonymous reporter must never do that. */
const IDENTITY_ASK = /\b(your name|who are you|phone|mobile|e-?mail|emirates id|contact details)\b|اسمك|الاسم الكامل|رقم الهاتف|هاتفك|جوالك|البريد الإلكتروني|بطاقة الهوية|رقم الهوية/i;

/**
 * The conversation with the reporter through the receipt code: every question the Department sent, every answer and file the reporter
 * returned, and a composer that suggests the questions the report leaves open. The reporter stays unknown – the questions are checked
 * so they never ask for an identity.
 */
export function ReporterThreadCard({ caseId, report }: { caseId: string; report: ReportGaps }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const thread = useQuery({ queryKey: ["wb-thread", caseId], queryFn: () => api.get<ThreadView>(`/api/whistleblowing/${caseId}/thread`) });
  useInvalidateOn(["case.updated", "document.updated"], [["wb-thread", caseId]]);
  const [question, setQuestion] = useState("");
  const [questionAr, setQuestionAr] = useState("");
  const ask = useSave({
    mutationFn: () => api.post(`/api/whistleblowing/${caseId}/ask-reporter`, { question: question.trim(), questionAr: questionAr.trim() || null }),
    saved: t("backoffice:report.ask.sent"),
    onSuccess: () => {
      setQuestion(""); setQuestionAr("");
      void qc.invalidateQueries({ queryKey: ["wb-thread", caseId] });
      void qc.invalidateQueries({ queryKey: ["case", caseId] });
    },
  });

  const suggestions = useMemo(() => {
    const keys: string[] = [];
    if (!report.observedFrom) keys.push("when");
    if (!report.evidenceDescription?.trim()) keys.push("evidence");
    if (report.allegedEntities.split(/[,،]\s*/).filter(Boolean).length <= 1) keys.push("others");
    keys.push("source");
    return keys.map((k) => ({ key: k, en: t(`backoffice:report.ask.suggestions.${k}`, { lng: "en" }), ar: t(`backoffice:report.ask.suggestions.${k}`, { lng: "ar" }) }));
  }, [report, t]);

  const asksIdentity = IDENTITY_ASK.test(question) || IDENTITY_ASK.test(questionAr);
  const tooShort = question.trim().length < 10;
  const v = thread.data;
  const ar = locale === "ar";

  return (
    <Card data-reporter-thread>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <MessageCircleQuestion className="size-4 text-aegold-700" /> {t("backoffice:report.ask.title")}
          {v?.awaitingReporter ? <Badge variant="warning" size="sm" dot>{t("backoffice:report.ask.awaiting")}</Badge> : null}
        </CardTitle>
        <CardDescription>{t("backoffice:report.ask.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {v && v.messages.length > 0 ? (
          <ol className="space-y-2">
            {v.messages.map((m) => (
              <li key={m.id} className={cn("flex", m.from === "ministry" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[85%] rounded-2xl px-3 py-2", m.from === "ministry" ? "bg-aegold-50 text-aegold-950" : "bg-sand-100")} data-from={m.from}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{m.from === "ministry" ? t("backoffice:report.ask.ministry") : t("backoffice:report.ask.reporter")} · {formatDateTime(m.at, locale)}</p>
                  <MarkdownBody className="prose-sm mt-0.5">{ar && m.bodyAr ? m.bodyAr : m.body}</MarkdownBody>
                </div>
              </li>
            ))}
          </ol>
        ) : <p className="text-xs text-muted-foreground">{t("backoffice:report.ask.empty")}</p>}

        {v && v.attachments.length > 0 ? (
          <div className="rounded-xl bg-sand-50 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:report.ask.attachments")}</p>
            <ul className="mt-1 space-y-1 text-xs">{v.attachments.map((a) => <li key={a.id} className="flex items-center gap-2"><Paperclip className="size-3 text-sand-500" /><span className="truncate" dir="auto">{a.fileName}</span><span className="text-muted-foreground">{formatDateTime(a.at, locale)}</span></li>)}</ul>
          </div>
        ) : null}

        <div className="space-y-2 border-t border-sand-200 pt-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-600"><Sparkles className="size-3 text-aegold-700" /> {t("backoffice:report.ask.suggested")}</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button key={s.key} type="button" data-suggestion={s.key} className="rounded-md border border-aegold-200 bg-white px-2.5 py-1 text-start text-xs text-aegold-900 hover:bg-aegold-50" onClick={() => { setQuestion(s.en); setQuestionAr(s.ar); }}>
                {ar ? s.ar : s.en}
              </button>
            ))}
          </div>
          <Field label={t("backoffice:report.ask.question")}>{(id) => <Textarea id={id} rows={3} maxLength={MAX_QUESTION} value={question} onChange={(e) => setQuestion(e.target.value)} dir="auto" draft={`case:${caseId}:reporter-thread:new:question`} />}</Field>
          <Field label={t("backoffice:report.ask.questionAr")} hint={t("backoffice:report.ask.questionArHint")}>{(id) => <Textarea id={id} rows={2} maxLength={MAX_QUESTION} value={questionAr} onChange={(e) => setQuestionAr(e.target.value)} dir="rtl" lang="ar" draft={`case:${caseId}:reporter-thread:new:questionAr`} />}</Field>
          {asksIdentity && report.isAnonymous ? <div data-identity-guard><InlineAlert tone="warning">{t("backoffice:report.ask.identityGuard")}</InlineAlert></div> : null}
          <div className="flex justify-end">
            <Button size="sm" variant="outline" disabled={tooShort || (asksIdentity && report.isAnonymous)} loading={ask.isPending} onClick={() => ask.mutate()}><Send className="rtl:-scale-x-100" /> {t("backoffice:report.ask.send")}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
